import { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import cn from 'bem-cn';

import { WebAppLayout } from '../../layouts/WebAppLayout';
import { RoutePaths } from '../../constants';
import { Stack, BackgroundImage, InfoBlock } from '../../components/UI';
import { HeaderElements } from '../../components/Header';
import { NavigationElements } from '../../components/Navigation';
import { PromptInfo, PromptInfoSkeleton } from '../../components/PromptInfo';
import { Feed, FeedPlace } from '../../components/Feed';
import { copyToClipboard } from '../../utils/clipboard/clipboard';
import { openAliceChat } from '../../utils/alice/alice';
import { isError404 } from '../../utils/error/isError';
import { useLike, useNavigateBack, useSlug, MetrikaGoal, useMetrika, usePrompt, useSimilarPrompts } from '../../hooks';
import { getImageUrl, ImageSize } from '../../utils/images';

import { usePromptShare } from './PromptPage.hooks';
import { PromptPageMeta } from './PromptPage.meta';
import './PromptPage.scss';

const b = cn('prompt-page');

export const PromptPage = () => {
    const { slug: promptId, isValidSlug: isValidPromptId } = useSlug();
    const { reachGoal } = useMetrika();
    const location = useLocation();

    const fromCollection = location.state?.fromCollection ?? false;

    // Используем оптимизированный хук для получения промпта
    const { data, isFetching, isError, isSuccess, error } = usePrompt(promptId);

    // Используем оптимизированный хук для похожих промптов
    const { data: similarPrompts, isFetching: isSimilarPromptsFetching } = useSimilarPrompts(promptId, {
        skip: !isValidPromptId || !data?.id,
    });

    const { likes, isLiked, toggleLike } = useLike({
        promptId,
        initialLikes: data?.likes || 0,
        initialIsLiked: data?.isLiked || false,
    });

    const handleBackButtonClick = useNavigateBack();
    const handleShareClick = usePromptShare(data);

    const isMediaPrompt = Boolean(data?.videoData.url);
    const isCharactersPrompt = data?.categories.includes('characters');

    const headerElements: HeaderElements = useMemo(
        () => ({
            desktop: {
                left: { type: 'logo' },
                right: { type: 'profile' },
            },
            mobile: {
                left: { type: 'backButton', onClick: handleBackButtonClick },
                center: { type: 'logo', className: b('mobile-header-logo') },
            },
        }),
        [handleBackButtonClick],
    );

    const navigationElements: NavigationElements = useMemo(() => {
        const buttonText = data?.buttonText || 'Попробовать в Алисе AI';

        const handleCopyToClipboard = () => {
            copyToClipboard(data?.prompt);
            reachGoal(MetrikaGoal.ClickCopyPrompt);
        };

        const handleOpenAliceChat = () => {
            openAliceChat(data?.id);
            reachGoal(MetrikaGoal.ClickoutChat);

            if (fromCollection) {
                reachGoal(MetrikaGoal.ClickoutChatFromCollection);
            }

            if (isCharactersPrompt) {
                reachGoal(MetrikaGoal.ClickoutChatFromCharacters);
            }
        };

        return {
            desktop: [
                {
                    text: 'Копировать промпт',
                    icon: 'copy',
                    onClick: handleCopyToClipboard,
                    usePopover: true,
                    popoverContent: 'Промпт скопирован',
                },
                {
                    text: buttonText,
                    className: b('desktop-nav-button-to-alice'),
                    onClick: handleOpenAliceChat,
                    accent: true,
                },
            ],
            mobile: [
                {
                    icon: 'copy',
                    className: b('mobile-nav-button-copy'),
                    onClick: handleCopyToClipboard,
                    usePopover: true,
                    popoverContent: 'Промпт скопирован',
                },
                {
                    text: buttonText,
                    className: b('mobile-nav-button-to-alice'),
                    onClick: handleOpenAliceChat,
                    accent: true,
                },
            ],
        };
    }, [data?.buttonText, data?.prompt, data?.id, reachGoal, fromCollection, isCharactersPrompt]);

    if (!isValidPromptId || (!isFetching && isError404(error))) {
        return <Navigate to={RoutePaths.NotFound} replace />;
    }

    return (
        <WebAppLayout headerElements={headerElements} navigationElements={navigationElements}>
            <Stack spacing={40} className={b()}>
                <BackgroundImage
                    className={b('background-image')}
                    image={getImageUrl(data?.imageData?.url, ImageSize.Big)}
                    mainColor={data?.imageData?.colors?.MainColor}
                />
                {isSuccess && <PromptPageMeta title={data.title} description={data.description} />}
                {isFetching && <PromptInfoSkeleton />}
                {isError && <InfoBlock text="Не удалось загрузить промпт. Попробуйте обновить страницу." isError />}
                {isSuccess && (
                    <PromptInfo
                        prompt={{ ...data, likes, isLiked }}
                        onLikeClick={toggleLike}
                        onShareClick={handleShareClick}
                        isMediaPrompt={isMediaPrompt}
                    />
                )}
                <Stack spacing={16}>
                    {(isSimilarPromptsFetching || Boolean(similarPrompts?.length)) && (
                        <h2 className={b('similar-title')}>Похожее</h2>
                    )}
                    <Feed
                        columns={2}
                        prompts={similarPrompts}
                        isLoading={isSimilarPromptsFetching}
                        place={FeedPlace.PromptPage}
                    />
                </Stack>
            </Stack>
        </WebAppLayout>
    );
};
